import * as React from 'react';
import {
  CreditCard,
  Search,
  LayoutDashboard,
  Users,
  GraduationCap,
  Zap,
  Moon,
  Sun,
  Laptop,
  Shield,
  Receipt,
  Image,
  Crown,
  BookOpen,
  Megaphone,
  Send,
  Languages,
  Globe,
  HelpCircle,
  FileText,
  Key,
  Bot,
  ShieldCheck,
  UserCog,
  UserPlus,
  Ticket,
  MessageSquare,
  Flag,
  AlertTriangle,
  Mail,
  BrainCircuit,
  ClipboardList,
  Coins,
  Settings,
} from 'lucide-react';

import {
  CommandDialog,
  CommandEmpty,
  CommandGroup,
  CommandItem,
  CommandList,
  CommandSeparator,
  CommandInput,
} from '@/components/ui/command';
import { useGo, useMenu } from '@refinedev/core';
import { useTheme } from 'next-themes';
import { Button } from '@/components/ui/button';

const iconMap: Record<string, React.ReactNode> = {
  dashboard: <LayoutDashboard className="mr-2 h-4 w-4" />,
  'admin-users': <UserCog className="mr-2 h-4 w-4" />,
  users: <Users className="mr-2 h-4 w-4" />,
  roles: <ShieldCheck className="mr-2 h-4 w-4" />,
  'user-invitations': <UserPlus className="mr-2 h-4 w-4" />,
  'user-entitlements': <ShieldCheck className="mr-2 h-4 w-4" />,
  subscriptions: <CreditCard className="mr-2 h-4 w-4" />,
  'subscription-plans': <Ticket className="mr-2 h-4 w-4" />,
  'channel-passes': <Ticket className="mr-2 h-4 w-4" />,
  payments: <CreditCard className="mr-2 h-4 w-4" />,
  orders: <Receipt className="mr-2 h-4 w-4" />,
  'admin/currencies': <Coins className="mr-2 h-4 w-4" />,
  courses: <GraduationCap className="mr-2 h-4 w-4" />,
  banners: <Image className="mr-2 h-4 w-4" />,
  teachers: <Crown className="mr-2 h-4 w-4" />,
  'club-settings': <Settings className="mr-2 h-4 w-4" />,
  posts: <FileText className="mr-2 h-4 w-4" />,
  moderation: <Shield className="mr-2 h-4 w-4" />,
  reports: <Flag className="mr-2 h-4 w-4" />,
  violations: <AlertTriangle className="mr-2 h-4 w-4" />,
  broadcasts: <Megaphone className="mr-2 h-4 w-4" />,
  triggers: <Zap className="mr-2 h-4 w-4" />,
  'message-templates': <Mail className="mr-2 h-4 w-4" />,
  'bot-tokens': <Bot className="mr-2 h-4 w-4" />,
  'translation-keys/bot': <Bot className="mr-2 h-4 w-4" />,
  'telegram-channels': <Send className="mr-2 h-4 w-4" />,
  'channel-memberships': <Users className="mr-2 h-4 w-4" />,
  'telegram-client': <MessageSquare className="mr-2 h-4 w-4" />,
  ai: <BrainCircuit className="mr-2 h-4 w-4" />,
  'knowledge-documents': <BookOpen className="mr-2 h-4 w-4" />,
  'audit-logs': <ClipboardList className="mr-2 h-4 w-4" />,
  languages: <Languages className="mr-2 h-4 w-4" />,
  'translation-keys/ui': <Globe className="mr-2 h-4 w-4" />,
  integrations: <Key className="mr-2 h-4 w-4" />,
  'support-tickets': <HelpCircle className="mr-2 h-4 w-4" />,
  messaging: <MessageSquare className="mr-2 h-4 w-4" />,
};

// Синхронизировано с sidebar.tsx
const menuGroups = {
  main: ['dashboard'],
  users: ['users', 'user-invitations', 'support-tickets', 'messaging'],
  content: ['teachers', 'banners', 'club-settings'],
  commerce: ['user-entitlements', 'courses', 'subscriptions', 'subscription-plans', 'channel-passes', 'orders', 'payments', 'admin/currencies', 'channel-memberships'],
  social: ['posts', 'moderation', 'reports', 'violations'],
  communications: ['broadcasts', 'triggers', 'message-templates'],
  telegram: ['bot-tokens', 'translation-keys/bot', 'telegram-channels', 'telegram-client'],
  analytics: ['ai', 'knowledge-documents'],
  admin: ['admin-users', 'roles', 'languages', 'translation-keys/ui', 'integrations', 'audit-logs'],
};

const groupLabels: Record<string, string> = {
  main: 'Главная',
  users: 'Пользователи',
  content: 'Контент',
  commerce: 'Коммерция',
  social: 'Соцсеть',
  communications: 'Рассылки',
  telegram: 'Telegram',
  analytics: 'AI',
  admin: 'Администрирование',
};

const customLabels: Record<string, string> = {
  'translation-keys/bot': 'Сообщения бота',
  'translation-keys/ui': 'Локализация UI',
  'admin/currencies': 'Валюты',
};

const Highlight = ({ text, query }: { text: string; query: string }) => {
  if (!query) { return text; }

  const escapedQuery = query.replaceAll(/[.*+?^${}()|[\]\\]/g, '\\$&');
  const parts = text.split(new RegExp(`(${escapedQuery})`, 'gi'));

  return (
    <span className="pointer-events-none">
      {parts.map((part, index) =>
        part.toLowerCase() === query.toLowerCase() ? (
          <span key={`${part}-${index}`} className="text-foreground font-medium">
            {part}
          </span>
        ) : (
          <span key={`${part}-${index}`} className="text-muted-foreground">
            {part}
          </span>
        )
      )}
    </span>
  );
};

export function GlobalSearch() {
  const [open, setOpen] = React.useState(false);
  const [search, setSearch] = React.useState('');
  const go = useGo();
  const { setTheme } = useTheme();
  const { menuItems } = useMenu();

  React.useEffect(() => {
    const down = (e: KeyboardEvent) => {
      if (e.key === 'k' && (e.metaKey || e.ctrlKey)) {
        e.preventDefault();
        setOpen((open) => !open);
      }
    };

    document.addEventListener('keydown', down);
    return () => document.removeEventListener('keydown', down);
  }, []);

  const runCommand = React.useCallback((command: () => unknown) => {
    setOpen(false);
    command();
  }, []);

  const getMenuItemsByGroup = (group: string) => {
    const groupNames = menuGroups[group as keyof typeof menuGroups] ?? [];
    return menuItems.filter((item) => groupNames.includes(item.name ?? ''));
  };

  const getLabel = (item: { name?: string; label?: string }) => {
    if (item.name && customLabels[item.name]) {
      return customLabels[item.name];
    }
    return item.label ?? '';
  };

  return (
    <>
      <Button
        variant="outline"
        className="relative h-9 w-full justify-start rounded-md bg-muted/50 text-sm font-normal text-muted-foreground shadow-none sm:pr-12 md:w-60 lg:w-96"
        onClick={() => setOpen(true)}
      >
        <Search className="mr-2 h-4 w-4" />
        <span className="inline-flex">Поиск...</span>
        <span className="pointer-events-none absolute right-1.5 top-1.5 hidden h-6 select-none items-center gap-1 rounded border bg-muted px-1.5 font-mono text-[10px] font-medium opacity-100 sm:flex">
          <span className="text-xs">⌘</span>K
        </span>
      </Button>
      <CommandDialog open={open} onOpenChange={setOpen}>
        <CommandInput
          placeholder="Введите команду или поиск..."
          value={search}
          onValueChange={setSearch}
        />
        <CommandList>
          <CommandEmpty>Ничего не найдено.</CommandEmpty>

          {Object.entries(menuGroups).map(([group]) => {
            const items = getMenuItemsByGroup(group);
            if (items.length === 0) {return null;}

            return (
              <React.Fragment key={group}>
                <CommandGroup heading={groupLabels[group]}>
                  {items.map((item) => {
                    const icon = iconMap[item.name ?? ''] ?? (
                      <FileText className="mr-2 h-4 w-4" />
                    );
                    const label = getLabel(item);
                    return (
                      <CommandItem
                        key={item.key}
                        value={`${label} ${item.name}`}
                        onSelect={() => {
                          runCommand(() =>
                            go({ to: item.route ?? `/${item.name}` })
                          );
                        }}
                      >
                        {icon}
                        <Highlight text={label} query={search} />
                      </CommandItem>
                    );
                  })}
                </CommandGroup>
                <CommandSeparator />
              </React.Fragment>
            );
          })}

          <CommandGroup heading="Тема">
            <CommandItem
              value="Светлая тема light"
              onSelect={() => runCommand(() => setTheme('light'))}
            >
              <Sun className="mr-2 h-4 w-4" />
              <Highlight text="Светлая" query={search} />
            </CommandItem>
            <CommandItem
              value="Тёмная тема dark"
              onSelect={() => runCommand(() => setTheme('dark'))}
            >
              <Moon className="mr-2 h-4 w-4" />
              <Highlight text="Тёмная" query={search} />
            </CommandItem>
            <CommandItem
              value="Системная тема system"
              onSelect={() => runCommand(() => setTheme('system'))}
            >
              <Laptop className="mr-2 h-4 w-4" />
              <Highlight text="Системная" query={search} />
            </CommandItem>
          </CommandGroup>
        </CommandList>
      </CommandDialog>
    </>
  );
}
